import { apiClient } from "../../lib/http/apiClient";
import { adminStorage } from "../admin/service";
import type {
  SubscriptionBlock,
  QuotaOverview,
  RefundAllowance,
  PlansResponse,
  CreateSubscriptionResponse,
  BillingHistoryResponse,
  CancellationReason,
  AdminGrantRequest,
  ConfigEntry,
  SubscribersListResponse,
  SubscribersListParams,
  TierLimitsResponse,
  TierPricing,
  TierDiscountRequest,
  CurrenciesResponse,
  FeatureCatalogResponse,
  FeatureCatalogEntry,
  PaymentOptions,
  MyUsageResponse,
} from "./types";

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const subscriptionsService = {
  getMySubscription: async (): Promise<SubscriptionBlock> => {
    const res = await apiClient.get("/api/v1/subscriptions/me");
    return res.data;
  },

  // Gateway-agnostic verify: actively queries the provider (Razorpay/Cashfree/
  // Stripe) and activates the local block if the provider says it's live. Use
  // this to poll after checkout so activation works without waiting for (or
  // ever receiving) the provider webhook.
  verifyStatus: async (): Promise<{ tier: string; status: string; active: boolean; gateway: string | null }> => {
    const res = await apiClient.get("/api/v1/subscriptions/me/status");
    return res.data;
  },

  getMyTier: async (refresh = false): Promise<{ tier: string }> => {
    const res = await apiClient.get("/api/v1/subscriptions/me/tier", {
      params: refresh ? { refresh: true } : {},
    });
    return res.data;
  },

  // Unified usage endpoint — every metered action's daily + monthly usage
  // against the user's effective tier. Pass `action` to scope to one
  // feature (accepts an action_type like "flyer"/"enquiry" or a UI tab
  // alias like "flyers"); omit for every action in one payload.
  getMyUsage: async (action?: string): Promise<MyUsageResponse> => {
    const res = await apiClient.get("/api/v1/subscriptions/me/usage", {
      params: action ? { action } : {},
    });
    return res.data;
  },

  getBillingHistory: async (limit = 50): Promise<BillingHistoryResponse> => {
    const res = await apiClient.get("/api/v1/subscriptions/me/billing", {
      params: { limit },
    });
    return res.data;
  },

  getPlans: async (): Promise<PlansResponse> => {
    const res = await apiClient.get("/api/v1/subscriptions/plans");
    return res.data;
  },

  getTierLimits: async (role: "seller" | "buyer"): Promise<TierLimitsResponse> => {
    const res = await apiClient.get("/api/v1/subscriptions/tier-limits", {
      params: { role },
    });
    return res.data;
  },

  getFeatureCatalog: async (): Promise<FeatureCatalogResponse> => {
    const res = await apiClient.get("/api/v1/subscriptions/feature-catalog");
    return res.data;
  },

  createSubscription: async (
    plan_key: string,
    gateway?: string
  ): Promise<CreateSubscriptionResponse> => {
    const res = await apiClient.post("/api/v1/subscriptions/create", {
      plan_key,
      ...(gateway ? { gateway } : {}),
    });
    return res.data;
  },

  getPaymentOptions: async (): Promise<PaymentOptions> => {
    const res = await apiClient.get("/api/v1/payments/options");
    return res.data;
  },

  upgrade: async (new_plan_key: string): Promise<{ message: string }> => {
    const res = await apiClient.post("/api/v1/subscriptions/upgrade", {
      new_plan_key,
    });
    return res.data;
  },

  downgrade: async (new_plan_key: string): Promise<{ message: string }> => {
    const res = await apiClient.post("/api/v1/subscriptions/downgrade", {
      new_plan_key,
    });
    return res.data;
  },

  cancel: async (
    reason: CancellationReason,
    note?: string
  ): Promise<{ message: string }> => {
    const res = await apiClient.post("/api/v1/subscriptions/cancel", {
      reason,
      note,
    });
    return res.data;
  },

  reactivate: async (): Promise<{ message: string }> => {
    const res = await apiClient.post("/api/v1/subscriptions/reactivate");
    return res.data;
  },

  confirmStripeCheckout: async (
    sessionId: string
  ): Promise<{ tier: string; status: string; already_active: boolean }> => {
    const res = await apiClient.post("/api/v1/subscriptions/me/confirm-stripe", null, {
      params: { session_id: sessionId },
    });
    return res.data;
  },

  // Admin endpoints — use adminHeaders() for super_admin JWT
  adminGetUser: async (userId: string): Promise<SubscriptionBlock> => {
    const res = await apiClient.get(
      `/api/admin/subscriptions/user/${userId}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminSearchUsers: async (q: string): Promise<{ users: {id: string, name: string, email: string}[] }> => {
    const res = await apiClient.get(
      `/api/admin/subscriptions/search-users`,
      { params: { q }, headers: adminHeaders() }
    );
    return res.data;
  },

  adminListSubscribers: async (params: SubscribersListParams = {}): Promise<SubscribersListResponse> => {
    const res = await apiClient.get("/api/admin/subscriptions/list", {
      params,
      headers: adminHeaders(),
    });
    return res.data;
  },

  adminGetUserEvents: async (
    userId: string,
    limit = 100
  ): Promise<BillingHistoryResponse> => {
    const res = await apiClient.get(
      `/api/admin/subscriptions/user/${userId}/events`,
      { params: { limit }, headers: adminHeaders() }
    );
    return res.data;
  },

  adminGrant: async (
    userId: string,
    payload: AdminGrantRequest
  ): Promise<{ message: string }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/grant/${userId}`,
      payload,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  /** Everything about one user's quota position in one call: per-action
   *  used / tier limit / admin extra / effective cap, plus the seller-only
   *  published-deal and deletion-refund blocks. */
  adminQuotaOverview: async (userId: string): Promise<QuotaOverview> => {
    const res = await apiClient.get(
      `/api/admin/subscriptions/quota-overview/${userId}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  /** Look up one seller's deletion-refund position. */
  adminGetRefundAllowance: async (userId: string): Promise<RefundAllowance> => {
    const res = await apiClient.get(
      `/api/admin/subscriptions/deal-refund-allowance/${userId}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  /** Nudge one seller's monthly deletion-refund cap. `delta` is signed:
   *  +3 to give more room, -2 to tighten. */
  adminAdjustRefundAllowance: async (
    userId: string,
    delta: number,
    reason: string
  ): Promise<{ delta: number; total_adjustment: number; effective_refund_limit: number | null }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/deal-refund-allowance/${userId}`,
      {},
      { headers: adminHeaders(), params: { delta, reason } }
    );
    return res.data;
  },

  adminSetRefundBlocked: async (
    userId: string,
    blocked: boolean,
    reason = ""
  ): Promise<{ deal_refund_blocked: boolean }> => {
    const url = `/api/admin/subscriptions/deal-refund-block/${userId}`;
    const res = blocked
      ? await apiClient.post(url, {}, { headers: adminHeaders(), params: { reason } })
      : await apiClient.delete(url, { headers: adminHeaders() });
    return res.data;
  },

  adminQuotaGrant: async (
    userId: string,
    payload: { action_type: string; extra_count: number; reason: string }
  ): Promise<{ action_type: string; extra_count_added: number; total_admin_extra: number }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/quota-grant/${userId}`,
      payload,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminQuotaRevoke: async (
    userId: string,
    actionType: string
  ): Promise<{ action_type: string; total_admin_extra: number }> => {
    const res = await apiClient.delete(
      `/api/admin/subscriptions/quota-grant/${userId}/${actionType}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminGrantUnlimitedQuota: async (userId: string, reason: string): Promise<{ unlimited_quota: boolean }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/unlimited-quota/${userId}`,
      {},
      { headers: adminHeaders(), params: { reason } }
    );
    return res.data;
  },

  adminRevokeUnlimitedQuota: async (userId: string): Promise<{ unlimited_quota: boolean }> => {
    const res = await apiClient.delete(
      `/api/admin/subscriptions/unlimited-quota/${userId}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminRevoke: async (userId: string): Promise<{ message: string }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/revoke/${userId}`,
      {},
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminReconcile: async (): Promise<{ message: string }> => {
    const res = await apiClient.post(
      "/api/admin/subscriptions/reconcile",
      {},
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminReconcileUser: async (
    userId: string
  ): Promise<{ message: string }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/reconcile/${userId}`,
      {},
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminBlockPhone: async (phone: string): Promise<{ message: string }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/trial-block/${phone}`,
      {},
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminUnblockPhone: async (phone: string): Promise<{ message: string }> => {
    const res = await apiClient.delete(
      `/api/admin/subscriptions/trial-block/${phone}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  // Admin config endpoints
  adminGetConfig: async (role?: string, tier?: string): Promise<{ configs: ConfigEntry[] }> => {
    const res = await apiClient.get("/api/admin/subscriptions/config", {
      headers: adminHeaders(),
      params: { ...(role ? { role } : {}), ...(tier !== undefined ? { tier } : {}) },
    });
    return res.data;
  },

  adminSetConfig: async (
    key: string,
    value: number
  ): Promise<{ message: string }> => {
    const res = await apiClient.put(
      `/api/admin/subscriptions/config/${key}`,
      { value },
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminResetConfig: async (key: string): Promise<{ message: string }> => {
    const res = await apiClient.delete(
      `/api/admin/subscriptions/config/${key}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminSetConfigDescription: async (
    key: string,
    description: string
  ): Promise<{ message: string }> => {
    const res = await apiClient.put(
      `/api/admin/subscriptions/config/${key}/description`,
      { description },
      { headers: adminHeaders() }
    );
    return res.data;
  },

  // Feature catalog endpoints (admin)
  adminGetFeatureCatalog: async (): Promise<FeatureCatalogResponse> => {
    const res = await apiClient.get("/api/admin/subscriptions/feature-catalog", {
      headers: adminHeaders(),
    });
    return res.data;
  },

  adminCreateFeatureCatalog: async (
    payload: { label: string; description: string }
  ): Promise<{ feature: FeatureCatalogEntry; message: string }> => {
    const res = await apiClient.post(
      `/api/admin/subscriptions/feature-catalog`,
      payload,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminSetFeatureCatalog: async (
    action: string,
    payload: { label?: string; description?: string }
  ): Promise<{ message: string }> => {
    const res = await apiClient.put(
      `/api/admin/subscriptions/feature-catalog/${action}`,
      payload,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminDeleteFeatureCatalog: async (action: string): Promise<{ message: string }> => {
    const res = await apiClient.delete(
      `/api/admin/subscriptions/feature-catalog/${action}`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  // Currency endpoints
  adminGetCurrencies: async (): Promise<CurrenciesResponse> => {
    const res = await apiClient.get("/api/admin/subscriptions/currencies", {
      headers: adminHeaders(),
    });
    return res.data;
  },

  adminSetCurrency: async (code: string): Promise<{ active: string }> => {
    const res = await apiClient.put(
      "/api/admin/subscriptions/currency",
      { code },
      { headers: adminHeaders() }
    );
    return res.data;
  },

  // Tier discount endpoints — apply to the MONEY price (list_price/final_price).
  // Independent of adminSetTierPriceCredits below, which sets the separate
  // credits-denominated price. See PriceBlock/TierCard: money price shows the
  // discount, credits price does not.
  adminSetTierDiscount: async (
    role: string,
    tier: string,
    payload: TierDiscountRequest
  ): Promise<TierPricing> => {
    const res = await apiClient.put(
      `/api/admin/subscriptions/pricing/${role}/${tier}/discount`,
      payload,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminClearTierDiscount: async (role: string, tier: string): Promise<TierPricing> => {
    const res = await apiClient.delete(
      `/api/admin/subscriptions/pricing/${role}/${tier}/discount`,
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminSetTierPriceCredits: async (role: string, tier: string, credits: number): Promise<{ role: string; tier: string; price_credits: number }> => {
    const res = await apiClient.post(
      "/api/admin/subscriptions/tier-price-credits",
      { role, tier, credits },
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminGetCreditRate: async (): Promise<{ rate_paise: number; rate_inr: number; updated_at: string | null; updated_by: string | null }> => {
    const res = await apiClient.get("/api/admin/subscriptions/credits/rate", {
      headers: adminHeaders(),
    });
    return res.data;
  },

  adminSetCreditRate: async (rate_paise: number): Promise<{ rate_paise: number; rate_inr: number }> => {
    const res = await apiClient.post(
      "/api/admin/subscriptions/credits/rate",
      { rate_paise },
      { headers: adminHeaders() }
    );
    return res.data;
  },

  adminGetGenerationCosts: async (): Promise<{
    costs_credits: Record<string, number>;
    rate_paise: number;
    costs_inr: Record<string, number>;
  }> => {
    const res = await apiClient.get("/api/admin/subscriptions/credits/generation-cost", {
      headers: adminHeaders(),
    });
    return res.data;
  },

  adminSetGenerationCost: async (
    action: "flyer" | "video" | "itinerary",
    credits: number
  ): Promise<{ action: string; credits: number }> => {
    const res = await apiClient.post(
      "/api/admin/subscriptions/credits/generation-cost",
      { action, credits },
      { headers: adminHeaders() }
    );
    return res.data;
  },
};
